import React, { useState } from 'react';
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { request } from './OperationsProvider';

const roleLabel = (role) => (role || '').replaceAll('_', ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

/**
 * Account administration. Creation already existed; without disable and password reset
 * an account could only be revoked by editing the database, which for a system holding
 * plant movement data is the gap that matters most.
 */
const UserTable = ({ rows, currentUserId, onChanged }) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reset, setReset] = useState(null);
  const [password, setPassword] = useState('');

  // `path` and `method` are here for the one action that is not an amendment to the account:
  // asking the person to confirm their address, which the server does rather than records.
  const act = async (user, body, message, path = null, method = null) => {
    setPending(true); setError(null); setNotice(null);
    try {
      await request(`/api/operations/${path || `users/${user.id}`}`, method || (path ? 'POST' : 'PATCH'), body || {});
      setNotice(message);
      await onChanged?.();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const submitReset = async (event) => {
    event.preventDefault();
    await act(reset, { password }, `Password reset for ${reset.name}. Their existing sessions were signed out.`);
    setReset(null); setPassword('');
  };

  return (
    <Stack gap={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Email', 'Role', 'Vehicle access', 'Status', ''].map((h, i) => (
                <TableCell key={h || `actions${i}`} align={h === '' ? 'right' : 'left'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              // An administrator disabling or demoting their own account would lock the
              // tenant out of its own console; the server refuses it, and the control is
              // withheld here so the refusal is never reached by accident.
              const self = row.id === currentUserId;
              const disabled = !!row.disabled;
              return (
                <TableRow key={row.id} hover sx={disabled ? { opacity: 0.6 } : undefined}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    {row.email}
                    {/* Addresses are typed by an administrator, so whether one reaches the person it
                        was typed for is worth saying on the row rather than finding out months later
                        when a report has been going nowhere. */}
                    {row.emailVerified
                      ? <Chip size="small" variant="outlined" color="success" label="Confirmed" sx={{ ml: 1 }} />
                      : row.verificationPending
                        ? <Chip size="small" variant="outlined" label="Awaiting confirmation" sx={{ ml: 1 }} />
                        : null}
                    {row.verificationError && <Typography variant="caption" color="error.main" display="block">{row.verificationError}</Typography>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{roleLabel(row.role)}</TableCell>
                  <TableCell>{row.vehicleIds?.includes('*') ? 'All vehicles' : `${row.vehicleIds?.length || 0} vehicles`}</TableCell>
                  <TableCell>
                    <Chip size="small" label={disabled ? 'Disabled' : 'Active'} color={disabled ? 'default' : 'success'} variant="outlined" />
                    {self && <Chip size="small" label="You" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {!row.emailVerified && row.role !== 'PLATFORM_ADMINISTRATOR' && (
                      <Button size="small" disabled={pending} onClick={() => act(row, null, `Confirmation sent to ${row.email}.`, `users/${row.id}/verify-email`)}>
                        {row.verificationPending ? 'Send again' : 'Confirm address'}
                      </Button>
                    )}
                    <Button size="small" disabled={pending} onClick={() => { setReset(row); setPassword(''); setError(null); }}>Reset password</Button>
                    <Button
                      size="small"
                      color={disabled ? 'primary' : 'error'}
                      disabled={pending || self}
                      onClick={() => act(row, { disabled: disabled ? 'false' : 'true' }, disabled ? `${row.name} re-enabled.` : `${row.name} disabled and signed out.`)}
                    >
                      {disabled ? 'Enable' : 'Disable'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary">
        Disabling takes effect on the account&apos;s next request, not their next sign-in, and ends any session they already hold. Resetting a password does the same.
      </Typography>

      <Dialog open={!!reset} onClose={() => !pending && setReset(null)} fullWidth maxWidth="xs">
        <Stack component="form" onSubmit={submitReset}>
          <DialogTitle>{`Reset password for ${reset?.name || ''}`}</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ pt: 1 }}>
              <TextField
                required
                autoFocus
                type="password"
                label="New password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                inputProps={{ minLength: 12, maxLength: 256 }}
                helperText="At least 12 characters. Give it to them over a channel other than email where you can."
              />
              <Alert severity="warning">This signs the account out everywhere it is currently in use.</Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={pending} onClick={() => setReset(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={pending}>Reset password</Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </Stack>
  );
};

export default UserTable;
